import React, { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import ClientAreaAuditLogModal from '@/components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { NavigationHeader } from '@/components/NavigationHeader/NavigationHeader'
import { CompanyTag } from '@/components/ui/badge'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { getSocialMediaTypesAction } from '@/store/wallet/wallet-actions'
import { isEmpty } from '@/utils/is-empty'
import { UpdatePaymentAgentProfileDto } from 'api/Wallet/Types/PaymentAgentDto'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import { useApiClient } from 'utils/api/ApiClient'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'

import { PaymentAgentForm, PaymentAgentFormSubmitValues } from './PaymentAgentForm'

const PaymentAgentDetailsPage = (): JSX.Element => {
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []
  const { id } = useParams<{ id: string }>()
  const [auditLogVisible, setAuditLogVisible] = useState(false)
  const walletsApiClient = useApiClient(WalletsApiClient)

  const socialMediaTypes = useAppSelector((state) => state.wallet.socialMediaTypes)

  const { callback: fetchPaymentAgents, forceRefresh } = useCallbackWithForceRefresh(
    () => (id ? walletsApiClient.getPaymentAgentById(id) : Promise.resolve(null)),
    [id]
  )

  const dispatch = useAppDispatch()

  const { data, isLoading } = useFetchOne(fetchPaymentAgents)

  const setToast = useContext(ToastContext)

  useEffect(() => {
    if (isEmpty(socialMediaTypes)) {
      dispatch(getSocialMediaTypesAction(walletsApiClient))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = async (values: PaymentAgentFormSubmitValues) => {
    try {
      await walletsApiClient.updatePaymentAgent(values)
      setToast(successToast('Payment Agent updated successfully.'))
    } catch (e) {
      console.error('Error updating payment agent', e)
    }
  }

  const handleSaveProfile = async (agentProfileDto: UpdatePaymentAgentProfileDto) => {
    try {
      if (agentProfileDto.id) {
        await walletsApiClient.updatePaymentAgentProfile({
          email: agentProfileDto.email,
          phone: agentProfileDto.phone,
          firstName: agentProfileDto.firstName,
          lastName: agentProfileDto.lastName,
          id: agentProfileDto.id,
          socialMedia: agentProfileDto.socialMedia,
        })
      } else {
        await walletsApiClient.createPaymentAgentProfile({
          paymentAgentId: data!.id,
          email: agentProfileDto.email,
          firstName: agentProfileDto.firstName,
          lastName: agentProfileDto.lastName,
          phone: agentProfileDto.phone,
        })
      }
    } catch (e) {
      console.error('Error', e)
    } finally {
      forceRefresh()
    }
  }

  const deleteProfile = async (paymentAgentProfileId: string) => {
    try {
      await walletsApiClient.deletePaymentAgentProfile(paymentAgentProfileId)
    } catch (e) {
      console.error(e)
    } finally {
      forceRefresh()
    }
  }

  if (isLoading) {
    return <Spinner />
  }
  if (!data) {
    return <></>
  }

  return (
    <React.Fragment>
      {auditLogVisible && (
        <ClientAreaAuditLogModal
          isVisible={auditLogVisible}
          onClose={() => setAuditLogVisible(false)}
          paymentAgentId={id}
        />
      )}
      <div>
        <Breadcrumbs crumbs={[{ title: 'Payment Agents', link: links.recordsPaymentAgents.url }]} />
        <NavigationHeader>
          {data.name && <CompanyTag id='ClientPaymentAgent'>{data.name}</CompanyTag>}
        </NavigationHeader>
        <PaymentAgentForm
          paymentAgent={data}
          permissions={permissions}
          onSubmit={onSubmit}
          forceRefresh={forceRefresh}
          handleSaveProfile={handleSaveProfile}
          deleteProfile={deleteProfile}
          setAuditLogModal={setAuditLogVisible}
          socialMediaTypes={socialMediaTypes}
        />
      </div>
    </React.Fragment>
  )
}

export default PaymentAgentDetailsPage
